import { useRef } from 'react';

import { queryOptions, useMutation, useSuspenseQuery } from '@tanstack/react-query';

import {
  type UpdateEstateAreas,
  updateEstateAreas,
} from '@/features/estates-property/api/estate-areas';
import { updateEstateDetails } from '@/features/estates-property/api/estate-details';
import { getProject, type Project } from '@/features/projects/api/projects';
import type { MutationConfig } from '@/lib/react-query';
import type { LoaderProps } from '@/lib/router';
import { api, extractData } from '@/lib/treaty';

import type { EstateSchemaType } from '../schemas/estate';

export type Estates = Awaited<ReturnType<typeof getEstates>>;

export type EstatesQueryFilters = Parameters<typeof api.estates.get>;
export type EstatesQuery = NonNullable<NonNullable<Parameters<typeof api.estates.get>[0]>['query']>;
export type EstatesQuerySort = NonNullable<EstatesQuery['sort']>;

export type CreateEstateType = Parameters<typeof api.estates.post>[0];

export type Estate = Estates[number];

type CreateEstateOptions = {
  mutationConfig?: MutationConfig<typeof createEstate>;
};

type EditEstateOptions = {
  mutationConfig?: MutationConfig<typeof editEstate>;
};

type DeleteEstateOptions = {
  mutationConfig?: MutationConfig<typeof deleteEstate>;
};

export const getEstates = (...args: EstatesQueryFilters) => {
  return extractData(api.estates.get(...args));
};

export const getEstatesQueryOptions = (...args: EstatesQueryFilters) => {
  return queryOptions({
    queryKey: ['estates', ...args],
    queryFn: () => getEstates(...args),
    placeholderData: (previousData) => previousData,
  });
};

export const ensureEstatesQueryData = ({ context: { queryClient } }: LoaderProps) => {
  return queryClient.ensureQueryData(getEstatesQueryOptions({ query: {} }));
};

export const useEstates = (...args: EstatesQueryFilters) => {
  return useSuspenseQuery({ ...getEstatesQueryOptions(...args) });
};

const createEstate = (data: CreateEstateType) => {
  return extractData(api.estates.post(data));
};

export const useCreateEstate = ({ mutationConfig }: CreateEstateOptions = {}) => {
  return useMutation({
    ...mutationConfig,
    mutationFn: createEstate,
  });
};

export const editEstate = ({ id, ...args }: EstateSchemaType & { id: string }) => {
  return extractData(api.estates({ id }).put(args));
};

export const useEditEstate = ({ mutationConfig }: EditEstateOptions = {}) => {
  return useMutation({
    ...mutationConfig,
    mutationFn: editEstate,
  });
};

const deleteEstate = (id: string) => {
  return extractData(api.estates({ id }).delete(id));
};

export const useDeleteEstate = ({ mutationConfig }: DeleteEstateOptions = {}) => {
  return useMutation({
    ...mutationConfig,
    mutationFn: deleteEstate,
  });
};

export const useCreateEstateWithProjectDetails = ({ mutationConfig }: CreateEstateOptions = {}) => {
  const projectDataRef = useRef<Project | null>(null);

  const mutationResult = useMutation({
    ...mutationConfig,
    mutationFn: createEstate,
    onSuccess: async (data, variables, onMutateResult, context) => {
      const [createdEstate] = data;
      const projectData = projectDataRef.current;
      if (projectData && createdEstate.projectId) {
        const promises: Promise<unknown>[] = [];
        if (projectData.details) {
          promises.push(
            updateEstateDetails({
              id: createdEstate.id,
              constructionType: '',
              condition: '',
              buildingYear: projectData.details.buildingYear || '',
              heatingType: [],
              heatGeneration: '',
              marketType: '',
              usageType: projectData.details.usageType || '',
              typeOfGlass: '',
              flooring: '',
              propertyType: '',
            })
          );
        }
        if (projectData.areaRooms) {
          // Seed areas with neutral defaults to avoid DB defaults (e.g., rooms=1)
          const updatedAreas: UpdateEstateAreas = {
            id: createdEstate.id,
            totalNumberOfRooms: 0,
          };
          if (
            projectData?.areaRooms.hasElevator !== null &&
            projectData.areaRooms.hasElevator !== undefined
          ) {
            updatedAreas.hasElevator = projectData.areaRooms.hasElevator;
          }
          if ('hasElevator' in updatedAreas) {
            promises.push(updateEstateAreas(updatedAreas));
          }
        }
        await Promise.all(promises);
      }
      mutationConfig?.onSuccess?.(data, variables, onMutateResult, context);
    },
  });

  return {
    ...mutationResult,
    mutateAsync: async (variables, options) => {
      if (variables.estateData.projectId) {
        const projectData = await getProject(variables.estateData.projectId);
        projectDataRef.current = projectData;
        variables.estateData = {
          ...variables.estateData,
          ...(projectData?.energyDetails?.id && {
            estateEnergyDetailsId: projectData.energyDetails.id,
          }),
        };
      }
      return mutationResult.mutateAsync(variables, options);
    },
  } as typeof mutationResult;
};
